import { Box, FormControl, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import Subheader from "../components/Subheader";
import Api from "../openapi";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { saveAs } from "file-saver";
import { DownloadIcon } from "../assets/icons";
import { defaultFormat, displayFormat } from "../utils";
import { LoadingButton } from "@mui/lab";
import { DateRangePicker } from "react-date-range";
import Can from "../components/Can";

export default function FinancialReport() {
  const [startDateDailyCash, setStartDateDailyCash] = useState(moment());
  const [endDateDailyCash, setEndDateDailyCash] = useState(moment());
  const [startDateDailySettlement, setStartDateDailySettlement] = useState(moment());
  const [endDateDailySettlement, setEndDateDailySettlement] = useState(moment());
  const [clientBalancesDownloadProcess, setClientBalancesDownloadProcess] = useState(false);
  const [dailyCashDownloadProcess, setDailyCashDownloadProcess] = useState(false);
  const [dailySettlementDownloadProcess, setDailySettlementDownloadProcess] = useState(false);

  const [state, setState] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const selectedDates = useMemo(() => {
    const startDate = moment(state[0].startDate);
    const endDate = moment(state[0].endDate);
    return { startDate, endDate };
  }, [state]);

  const btnEnableDailyCash = useMemo(() => {
    return selectedDates.startDate.isValid() && selectedDates.endDate.isValid();
  }, [selectedDates.startDate, selectedDates.endDate]);

  const btnEnableDailySettlement = useMemo(() => {
    return selectedDates.startDate.isValid() && selectedDates.endDate.isValid();
  }, [selectedDates.startDate, selectedDates.endDate]);

  const clientBalancesDownload = async () => {
    setClientBalancesDownloadProcess(true);
    try {
      const res = await Api.AccountReport.generateClientBalancesReport({
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,",
      });
      saveAs(blob, `client-balances-${startDateDailyCash.format(defaultFormat)}.xlsx`);
    } catch (error) {}
    setClientBalancesDownloadProcess(false);
  };
  const dailyCashDownload = async () => {
    setDailyCashDownloadProcess(true);
    try {
      const res = await Api.AccountReport.generateDailyCashReport(
        selectedDates.startDate.format(defaultFormat),
        selectedDates.endDate.format(defaultFormat),
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,",
      });
      saveAs(blob, `daily-cash-${moment().format(defaultFormat)}.xlsx`);
    } catch (error) {}
    setDailyCashDownloadProcess(false);
  };
  const dailySettlementDownload = async () => {
    setDailySettlementDownloadProcess(true);
    try {
      const res = await Api.AccountReport.generateClientSettlementReport(
        selectedDates.startDate.format(defaultFormat),
        selectedDates.endDate.format(defaultFormat),
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,",
      });
      saveAs(blob, `daily-settlement-${moment().format(defaultFormat)}.xlsx`);
    } catch (error) {}
    setDailySettlementDownloadProcess(false);
  };

  return (
    <Box className="delivery-report">
      <Box className="delivery-report-upper">
        <Box className="delivery-report-upper-left">
          <Subheader page="Financial Reports" />
        </Box>
      </Box>
      <Box
        sx={{
          padding: "24px",
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
          display: "flex",
          gap: "24px",
        }}
      >
        <Box>
          <DateRangePicker
            onChange={(item: any) => setState([item.selection])}
            showSelectionPreview={true}
            moveRangeOnFirstSelection={false}
            months={2}
            ranges={state}
            direction="horizontal"
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>Daily Cash</Typography>
            <Typography sx={{ fontSize: "12px", fontWeight: 400, marginBottom: "4px" }}>
              Click to download the report for {selectedDates.startDate.format(displayFormat)} -{" "}
              {selectedDates.endDate.format(displayFormat)}
            </Typography>
            <Can permission="FINANCIAL_REPORT_CREATE">
              <LoadingButton
                disabled={!btnEnableDailyCash}
                loading={dailyCashDownloadProcess}
                onClick={dailyCashDownload}
                endIcon={<DownloadIcon />}
                sx={{
                  "&, &:hover": { boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)" },
                  background: "#f9f9f9",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "normal",
                  display: "flex",
                  padding: "12px",
                  "&": { color: "text.dark2" },
                  "&:hover": { background: "#efefef" },
                }}
              >
                Download
              </LoadingButton>
            </Can>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>Daily Settlement</Typography>
            <Typography sx={{ fontSize: "12px", fontWeight: 400, marginBottom: "4px" }}>
              Click to download the report for {selectedDates.startDate.format(displayFormat)} -{" "}
              {selectedDates.endDate.format(displayFormat)}
            </Typography>
            <Can permission="FINANCIAL_REPORT_CREATE">
              <LoadingButton
                disabled={!btnEnableDailySettlement}
                loading={dailySettlementDownloadProcess}
                onClick={dailySettlementDownload}
                endIcon={<DownloadIcon />}
                sx={{
                  "&, &:hover": { boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)" },
                  background: "#f9f9f9",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "normal",
                  display: "flex",
                  padding: "12px",
                  "&": { color: "text.dark2" },
                  "&:hover": { background: "#efefef" },
                }}
              >
                Download
              </LoadingButton>
            </Can>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography sx={{ fontWeight: 600 }}>Client Balances</Typography>
            <Typography sx={{ fontSize: "12px", fontWeight: 400, marginBottom: "4px" }}>
              Click to download the report
            </Typography>
            <Can permission="FINANCIAL_REPORT_CREATE">
              <LoadingButton
                loading={clientBalancesDownloadProcess}
                onClick={clientBalancesDownload}
                endIcon={<DownloadIcon />}
                sx={{
                  "&, &:hover": { boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)" },
                  background: "#f9f9f9",
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "normal",
                  display: "flex",
                  padding: "12px",
                  "&": { color: "text.dark2" },
                  "&:hover": { background: "#efefef" },
                }}
              >
                Download
              </LoadingButton>
            </Can>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
